import type { Language } from './enums';

/** Template merge engine (M3, D32) — shared by the Worker (draft generation) and the web app
 *  (Templates screen live preview), so both always render identically. */

export const TEMPLATE_FIELDS = [
  'owner_name',
  'business_name',
  'contract_end_date',
  'feature_name',
  // the message library's own merge fields (D88)
  'first_name',
  'city',
  'street_name',
  'street_address',
  'business_type',
  'incumbent',
] as const;
export type TemplateField = (typeof TEMPLATE_FIELDS)[number];

export type MergeFields = Partial<Record<TemplateField, string>>;

const TOKEN = new RegExp(`\\{(${TEMPLATE_FIELDS.join('|')})\\}`, 'g');

/** Replace {field} tokens for the four known fields in one pass, so a value that itself holds a token
 *  stays as it is; unknown tokens are left untouched. */
export const renderTemplate = (text: string, fields: MergeFields): string =>
  text.replace(TOKEN, (_, f: TemplateField) => fields[f] ?? '');

const MONTHS: Record<Language, string[]> = {
  en: [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ],
  es: [
    'enero',
    'febrero',
    'marzo',
    'abril',
    'mayo',
    'junio',
    'julio',
    'agosto',
    'septiembre',
    'octubre',
    'noviembre',
    'diciembre',
  ],
  pt: [
    'janeiro',
    'fevereiro',
    'março',
    'abril',
    'maio',
    'junho',
    'julho',
    'agosto',
    'setembro',
    'outubro',
    'novembro',
    'dezembro',
  ],
};

/** "2026-10-01" → "October 1, 2026" / "1 de octubre de 2026" / "1 de outubro de 2026".
 *  Falls back to the raw string when the input is not a calendar date. */
export const formatDateForLanguage = (isoDate: string, language: Language): string => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(isoDate);
  if (!m) return isoDate;
  const year = Number(m[1]);
  const month = MONTHS[language][Number(m[2]) - 1];
  const day = Number(m[3]);
  if (!month) return isoDate;
  return language === 'en' ? `${month} ${day}, ${year}` : `${day} de ${month} de ${year}`;
};

/** Whole calendar days from `from` to `to` (positive when `to` is later). Pure UTC math — safe on
 *  both the Worker and the device; callers supply the Pacific calendar day (D5 amended 2026-10-26). */
export const calendarDaysBetween = (to: string, from: string): number => {
  const p = (iso: string): number => {
    const [y, m, d] = iso.split('-').map(Number);
    return Date.UTC(y ?? 1970, (m ?? 1) - 1, d ?? 1);
  };
  return Math.round((p(to) - p(from)) / 86_400_000);
};

export type ContractMilestone = 'contract_30' | 'contract_60';

/** D32: the TIGHTEST applicable milestone. ≤30 days → contract_30; 31–60 → contract_60;
 *  expired (<0) or >60 → none. A lead first seen at 25 days gets ONE draft (30), not two. */
export const contractMilestoneFor = (contractEndDate: string, today: string): ContractMilestone | null => {
  const left = calendarDaysBetween(contractEndDate, today);
  if (left < 0) return null;
  if (left <= 30) return 'contract_30';
  if (left <= 60) return 'contract_60';
  return null;
};
